import { useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, CircularProgress, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography,
} from '@mui/material';
import { History, Preview } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { TEST_STATUS_COLORS } from '../../constants/testStatusColors';
import { formatDepthRange, timeAgo } from './testsPageUtils';
import type { TestListingRow } from '../../services/testListingService';
import type { ReportArchiveRollup } from '../../types/reportArtifact';

export interface TestsTableProps {
  rows: TestListingRow[];
  rollups: Map<string, ReportArchiveRollup>;
  loading: boolean;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  selected: Set<string>;
  onToggleRow: (wodKey: string) => void;
  onToggleAll: (keys: string[]) => void;
  onOpenTest: (row: TestListingRow) => void;
  onPreview: (row: TestListingRow) => void;
  onHistory: (row: TestListingRow) => void;
}

const HEADERS: Array<{ field: string; label: string; width?: number; sortable?: boolean }> = [
  { field: 'SampleReference', label: 'Sample', width: 110, sortable: true },
  { field: 'TestName', label: 'Test', sortable: true },
  { field: 'TestStatus', label: 'Status', width: 140, sortable: true },
  { field: 'ProjectName', label: 'Project', sortable: true },
  { field: 'WorkOrderId', label: 'Work Order', width: 115, sortable: true },
  { field: 'LocationId', label: 'Location', width: 100, sortable: true },
  { field: 'DepthTop', label: 'Depth', width: 105, sortable: true },
  { field: 'CheckedBy', label: 'Tested By', width: 105, sortable: true },
  { field: 'reports', label: 'Reports', width: 110 },
  { field: 'sysLastModifiedDate', label: 'Updated', width: 90, sortable: true },
  { field: 'actions', label: '', width: 90 },
];

/** Admin-grade tests table: status chips with the real test-status colors,
 *  report-archive rollup per row, selection for bulk actions, and per-row
 *  preview / archive shortcuts. Client-paginated (the endpoint caps at 500). */
export const TestsTable = ({
  rows, rollups, loading, sortBy, sortDir, onSort, selected,
  onToggleRow, onToggleAll, onOpenTest, onPreview, onHistory,
}: TestsTableProps) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );
  const pageKeys = paged.map((r) => r.WorkOrderDetailKey);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const somePageSelected = pageKeys.some((k) => selected.has(k));

  if (page > 0 && page * rowsPerPage >= rows.length) {
    // Filter shrank the list below the current page — snap back.
    setPage(0);
  }

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', overflow: 'hidden' }}>
      {/* The TABLE CONTAINER is the scroll region (not the page): that makes
          stickyHeader anchor to the container's own top, immune to ancestor
          overflow (AppShell's <main> uses overflow-x:hidden, which per spec
          forces overflow-y to compute to auto — a document-scroll sticky header
          would strand mid-list against it). Height fills the viewport below the
          compact KPI strip + filters + page chrome. */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', minHeight: 300 }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            '& thead th': { py: 0.5, px: 1.25, zIndex: 2 },
            '& tbody td': { py: 0.3, px: 1.25 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ backgroundColor: theme.palette.background.paper }}>
                <Checkbox
                  size="small"
                  indeterminate={somePageSelected && !allPageSelected}
                  checked={allPageSelected}
                  onChange={() => onToggleAll(pageKeys)}
                  inputProps={{ 'aria-label': 'Select all tests on this page' }}
                />
              </TableCell>
              {HEADERS.map((h) => (
                <TableCell
                  key={h.field}
                  sx={{
                    width: h.width,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {h.sortable ? (
                    <TableSortLabel
                      active={sortBy === h.field}
                      direction={sortBy === h.field ? sortDir : 'asc'}
                      onClick={() => onSort(h.field)}
                    >
                      {h.label}
                    </TableSortLabel>
                  ) : h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={HEADERS.length + 1} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            )}
            {!loading && paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={HEADERS.length + 1} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tests match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && paged.map((row) => {
              const statusColor = TEST_STATUS_COLORS[row.TestStatus] ?? theme.palette.text.secondary;
              const rollup = rollups.get(row.WorkOrderDetailKey);
              const isSelected = selected.has(row.WorkOrderDetailKey);
              return (
                <TableRow
                  key={row.WorkOrderDetailKey}
                  hover
                  selected={isSelected}
                  onClick={() => onOpenTest(row)}
                  sx={{ cursor: 'pointer', '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.06) } }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => onToggleRow(row.WorkOrderDetailKey)}
                      inputProps={{ 'aria-label': `Select ${row.SampleReference}` }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.SampleReference || row.SampleId}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 230, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Tooltip title={row.TestName}><span>{row.TestName}</span></Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.TestStatus}
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: statusColor,
                        backgroundColor: alpha(statusColor, 0.14),
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 190, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Tooltip title={`${row.ProjectName}${row.ProjectId ? ` (${row.ProjectId})` : ''}`}>
                      <span>{row.ProjectName}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.WorkOrderId}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 110, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.LocationId}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {formatDepthRange(row.DepthTop, row.DepthBase)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{row.CheckedBy || '—'}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {rollup ? (
                      <Tooltip title={`${rollup.totalCount} archived (${rollup.issuedCount} issued) — click for history`}>
                        <Chip
                          size="small"
                          clickable
                          onClick={() => onHistory(row)}
                          label={rollup.issuedCount > 0
                            ? `${rollup.totalCount} · ${rollup.issuedCount} issued`
                            : `${rollup.totalCount}`}
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: rollup.issuedCount > 0 ? theme.customColors.maroon : theme.palette.primary.dark,
                            backgroundColor: alpha(
                              rollup.issuedCount > 0 ? theme.customColors.maroon : theme.palette.primary.main,
                              0.12,
                            ),
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {timeAgo(row.sysLastModifiedDate)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Preview report">
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onPreview(row)} aria-label="Preview report">
                        <Preview sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Report archive">
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onHistory(row)} aria-label="Report archive">
                        <History sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          sx={{ '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
        />
      </Box>
    </Paper>
  );
};
